import axios from 'axios';
import { client } from '@/services/api/axios.ts';
import { useQuery } from '@tanstack/react-query';
import { RangeGranularity } from '@/features/dashboard/hooks/queries/get-overview.ts';

export type TEnergyManagement = {
  Battery_Charge: number;
  Battery_Discharge: number;
  Battery_State_of_Charge: number;
  time: string;
}[];

export async function getChartDeviceQuery({
  dateRange,
  deviceId,
}: {
  deviceId: string | undefined;
  dateRange: {
    startDate: Date;
    endDate: Date | undefined;
    rangeGranularity: RangeGranularity;
  };
}): Promise<TEnergyManagement | undefined | never> {
  if (!deviceId) return undefined;

  const searchParams = new URLSearchParams();
  searchParams.set('granularity', dateRange.rangeGranularity);
  searchParams.set('startDate', `${new Date(dateRange.startDate).getTime()}`);
  searchParams.set('endDate', `${dateRange.endDate && new Date(dateRange.endDate).getTime()}`);

  try {
    const { data } = await client.get<{ body: TEnergyManagement | undefined }>(
      `/devices/${deviceId}/charts?${searchParams.toString()}`
    );

    return data.body;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(`${error?.response?.data?.message}`);
    }

    if (error instanceof Error) {
      throw new Error(`${error.message}}`);
    }
  }
}

export const useGetChartDeviceQuery = ({
  deviceId,
  dateRange,
  enabled = true,
}: {
  deviceId: string | undefined;
  dateRange: {
    startDate: Date;
    endDate: Date | undefined;
    rangeGranularity: RangeGranularity;
  };
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [
      'devices',
      deviceId,
      'charts',
      dateRange.rangeGranularity,
      dateRange.startDate,
      dateRange.endDate,
    ],
    queryFn: () => getChartDeviceQuery({ deviceId, dateRange }),
    retry: 1,
    staleTime: 1000 * 60,
    enabled,
  });
};
